import { Link } from 'react-router-dom'
import { Keyboard, LayoutGrid } from 'lucide-react'
import { useT, useI18n, LANG_LABELS, type Lang } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { useTheme, type Theme } from '@/providers/UiProviders'
import { getIdentity, clearSession } from '@/lib/auth'
import { MonoValue } from '@/components/DataDisplay'
import { StatusBadge, Tag } from '@/components/StatusBadge'
import { cn } from '@/lib/cn'

/**
 * A28.01 — Mening profilim. Klient-tomon admin-identity (JWT payload'idan,
 * `getIdentity`). Server-tomon `/admin/me` qurilgach kengaytiriladi (RBAC kontrakti).
 */
export function MyProfile() {
  const t = useT()
  const id = getIdentity()
  return (
    <>
      <PageHeader title={t.account.profile} screenId="A28.01" />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {!id ? (
          <div className="text-sm text-tertiary">—</div>
        ) : (
          <div className="max-w-xl rounded-lg border border-border-default bg-surface-2 p-4">
            <div className="mb-3">
              <StatusBadge status="active" />
            </div>
            <Row label={t.account.username}>
              <MonoValue value={id.username} />
            </Row>
            <Row label={t.account.role}>{id.role}</Row>
            {id.roles && id.roles.length > 0 && (
              <Row label={t.account.roles}>
                <span className="flex flex-wrap gap-1">
                  {id.roles.map((r) => (
                    <Tag key={r}>{r}</Tag>
                  ))}
                </span>
              </Row>
            )}
            <div className="mt-4">
              <button
                onClick={() => {
                  clearSession()
                  window.location.href = '/v2/'
                }}
                className="rounded-md border border-border-default bg-surface-1 px-3 py-1.5 text-sm text-secondary hover:border-danger hover:text-danger"
              >
                {t.account.logout}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

/** A28.04 — Interfeys sozlamalari: til + mavzu (klient, localStorage'da saqlanadi). */
export function InterfaceSettings() {
  const t = useT()
  const { lang, setLang } = useI18n()
  const { theme, setTheme } = useTheme()
  const langs: Lang[] = ['uz', 'ru', 'en']
  const themes: { v: Theme; label: string }[] = [
    { v: 'dark', label: t.account.dark },
    { v: 'light', label: t.account.light },
  ]
  return (
    <>
      <PageHeader title={t.account.interface} screenId="A28.04" />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="max-w-xl space-y-4">
          <Setting label={t.account.language}>
            <div className="flex gap-1.5">
              {langs.map((l) => (
                <button
                  key={l}
                  onClick={() => setLang(l)}
                  className={cn(
                    'rounded-md border px-3 py-1.5 text-sm',
                    lang === l
                      ? 'border-accent bg-accent-subtle text-accent'
                      : 'border-border-default bg-surface-1 text-secondary hover:text-primary',
                  )}
                >
                  {LANG_LABELS[l]}
                </button>
              ))}
            </div>
          </Setting>
          <Setting label={t.account.theme}>
            <div className="flex gap-1.5">
              {themes.map((th) => (
                <button
                  key={th.v}
                  onClick={() => setTheme(th.v)}
                  className={cn(
                    'rounded-md border px-3 py-1.5 text-sm',
                    theme === th.v
                      ? 'border-accent bg-accent-subtle text-accent'
                      : 'border-border-default bg-surface-1 text-secondary hover:text-primary',
                  )}
                >
                  {th.label}
                </button>
              ))}
            </div>
          </Setting>
        </div>
      </div>
    </>
  )
}

/** A28.06 — Klaviatura yorliqlari (grom-admin-design §6 — statik ma'lumotnoma). */
export function Shortcuts() {
  const t = useT()
  const rows: { keys: string; action: string }[] = [
    { keys: 'j / ↓', action: t.account.scNext },
    { keys: 'k / ↑', action: t.account.scPrev },
    { keys: 'x', action: t.account.scSelect },
    { keys: 'Shift+x', action: t.account.scRange },
    { keys: 'Enter', action: t.account.scOpen },
    { keys: 'Space', action: t.account.scPanel },
    { keys: '⌘K', action: t.account.scSearch },
    { keys: '/', action: t.account.scFilter },
    { keys: 'Esc', action: t.account.scClose },
    { keys: '?', action: t.account.scHelp },
  ]
  return (
    <>
      <PageHeader title={t.account.shortcuts} screenId="A28.06" />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="max-w-xl overflow-hidden rounded-md border border-border-default">
          {rows.map((r, i) => (
            <div
              key={r.keys}
              className={cn(
                'flex items-center gap-4 px-3 py-2 text-sm',
                i > 0 && 'border-t border-border-subtle',
              )}
            >
              <kbd className="w-24 shrink-0 font-mono text-xs text-accent">{r.keys}</kbd>
              <span className="text-secondary">{r.action}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  )
}

/** A28.05 — Yordam va qo'llanma. Panel haqida + asosiy kirish nuqtalari. */
export function Help() {
  const t = useT()
  return (
    <>
      <PageHeader title={t.account.help} screenId="A28.05" />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="max-w-2xl space-y-3">
          <div className="rounded-lg border border-border-default bg-surface-2 p-4">
            <p className="text-sm text-secondary">{t.account.helpIntro}</p>
          </div>
          <Link
            to="/modules"
            className="flex items-center gap-3 rounded-lg border border-border-default bg-surface-2 p-3 hover:border-accent"
          >
            <LayoutGrid size={18} className="text-accent" />
            <div>
              <div className="text-sm font-medium text-primary">{t.nav.modules}</div>
              <div className="text-xs text-tertiary">{t.account.helpModules}</div>
            </div>
          </Link>
          <Link
            to="/account/shortcuts"
            className="flex items-center gap-3 rounded-lg border border-border-default bg-surface-2 p-3 hover:border-accent"
          >
            <Keyboard size={18} className="text-accent" />
            <div>
              <div className="text-sm font-medium text-primary">{t.account.shortcuts}</div>
              <div className="text-xs text-tertiary">{t.account.helpShortcuts}</div>
            </div>
          </Link>
        </div>
      </div>
    </>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-4 border-b border-border-subtle py-2 last:border-0">
      <span className="w-32 shrink-0 text-sm text-tertiary">{label}</span>
      <span className="min-w-0 flex-1 text-sm text-primary">{children}</span>
    </div>
  )
}

function Setting({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-border-default bg-surface-2 p-3">
      <div className="mb-2 text-sm font-medium text-secondary">{label}</div>
      {children}
    </div>
  )
}
